import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiBaseRoute } from './consts';
import { QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { router } from '../utils/route';

export const apiInstance = axios.create({
  baseURL: ApiBaseRoute,
  withCredentials: true,
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.status === 403 || error.data?.name == 'google_reconnect_required') {
          return false;
        }

        return failureCount < 1;
      },
    },
  },
});

// Queue to handle multiple requests during token refresh
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('googleAccessToken');
  localStorage.removeItem('businessId');
  localStorage.removeItem('userPlanTypes');
  localStorage.removeItem('impersonation');
  localStorage.removeItem('adminToken');
  queryClient.clear();
  window.location.href = '/login';
};

apiInstance.interceptors.request.use(
  (config) => {
    if (!config.headers.get('Authorization')) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      config.headers.set('ECHO-Business-ID', businessId);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Reject 403 permission errors without redirect (unlike 401)
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    // Show error for plan exeeded error
    const errorData = error.response?.data as any;
    console.log('errorData', errorData);
    if (errorData?.name == 'plan_exeeded' && errorData.details.limitName !== 'businessLimit') {
      // businessLimit could not be redirected to plan because it is trigerred while creating new business and no current business is selected
      toast.warning(errorData?.message + ' Click to upgrade your plan!', {
        onClick: () => {
          router.navigate('/settings?tab=plan');
        },
        autoClose: false,
        position: 'top-center',
        toastId: `plan_exeeded-${errorData.details.limitName}`,
      });

      return Promise.reject(error?.response);
    }

    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error?.response);
    }

    // Show error for google reconnect required error
    if (error.response?.status == 401 && errorData?.name == 'google_reconnect_required') {
      toast.error(errorData?.message, { autoClose: 12000 });

      return Promise.reject(error?.response);
    }

    // Don't try to refresh if we're already on the refresh endpoint
    if (originalRequest.url?.includes('/auth/refresh')) {
      clearAuthAndRedirect();
      return Promise.reject(error?.response);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (token) {
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
          }
          return apiInstance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the token
      const response = await apiInstance.get('/auth/refresh');
      const { accessToken } = response.data;

      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
        processQueue(null, accessToken);
        return apiInstance(originalRequest);
      }

      throw new Error('No access token received');
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
